import { memo, useEffect, useState, MouseEvent as ReactMouseEvent, useCallback } from 'react';
import { Spacings } from '@picsart/cascade';
import Layout from '@picsart/editorlayout/Layout';
import DynamicContainer from '@picsart/editorlayout/DynamicContainer';
import Grid from '@picsart/grid/Grid';
import { v4 as uuid } from 'uuid';
import { useDispatch } from 'react-redux';
import { getContext } from '@picsart/miniapps-sdk';
import { FileRejection } from 'react-dropzone';

import { ENVIRONMENT, appEnvironment, BATCH_EDITOR_FIRST_OPEN, IS_ONE_TOOL, ONE_TOOL_NAME } from 'configs/constants';
import Cookies from 'services/wrappers/cookies';
import UploadDropzone from 'pages/Batch/components/UploadDropzone';
import useUnMount from 'hooks/common/useUnMount';
import useMemoSelector from 'hooks/useMemoSelector';
import { getBatchEraserItem } from 'store/batch/selectors';
import useBatchDeeplinks from 'hooks/useBatchDeeplinks';
import batchSlice from 'store/batch';
import { batchEditorInfoPopupData } from 'configs/infoPopupData';
import InfoPopup from 'components/common/InfoPopup';
import useMount from 'hooks/common/useMount';
import { getUserData } from 'store/selectors/general';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import useFetchCardsAndBackgrounds from 'hooks/useFetchCardsAndBackgrounds';
import generalSlice from 'store/general';
import useFileUploader from 'hooks/useFileUploader';
import { isOSThemeSupported } from 'utils/ui-helpers';
import useZoom from 'hooks/useZoom';

import Header from './sections/Header';
import Sidebar from './sections/Sidebar';
import MainContent from './sections/MainContent';
import SuccessNotificationUI from './components/SuccessNotificationUI';
import BatchEraser from './components/BatchEraser';
import analytics from './utils/analytics';
import { ALLOWED_LIMIT_FILES, BATCH_TOOLS, batchToolsList } from './configs';
import SidebarTools from './sections/Sidebar/SidebarTools/SidebarTools';
import PreviewModal from './components/PreviewModal';
import ThemeSettings from './components/ThemeSettings';

function Batch() {
  const dispatch = useDispatch();
  const { isSubscribed, waitingUserSubscription } = useMemoSelector(getUserData);
  const { activeTool, setExpandState, bottomSheetCustomHeight, acceptFiles, setActiveTool, setBottomSheetCustomHeight } = useBatchData();
  const onFileUploadDone = useCallback((files: File[], rejectedFiles: FileRejection[], isDrop?: boolean) => {
    acceptFiles(files, rejectedFiles, isDrop, () => {
      if (!IS_ONE_TOOL) {
        return;
      }

      const currentActiveTool = ONE_TOOL_NAME as BATCH_TOOLS;
      const currentToolConfig = batchToolsList.find(el => el.type === currentActiveTool);
      if (!currentToolConfig) {
        return;
      }
      const bottomSheetHeight = currentToolConfig?.bottomSheetHeight || bottomSheetCustomHeight;
      setBottomSheetCustomHeight(bottomSheetHeight);
      setActiveTool(currentActiveTool);
      if (currentToolConfig.isPremium) {
        dispatch(batchSlice.actions.setAppliedPremiumTool(currentActiveTool));
      }
    });
  }, [acceptFiles, bottomSheetCustomHeight, dispatch, setActiveTool, setBottomSheetCustomHeight]);

  const { handleUploadClick } = useFileUploader({
    onFileUploadDone,
    maxCount: ALLOWED_LIMIT_FILES,
  });

  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const isWeb = getContext().handlers.isWeb();

  const eraserItem = useMemoSelector(getBatchEraserItem);
  const [isDragActive, setIsDragActive] = useState(false);
  const isBatchEditorFirstOpen = Cookies.get(BATCH_EDITOR_FIRST_OPEN) === 'true';

  useZoom();

  useUnMount(() => batchSlice.actions.reset());

  useMount(() => sessionStorage.setItem('session_id', uuid()));
  // open file chooser for mobile
  useMount(() => {
    !isWeb && handleUploadClick({} as ReactMouseEvent<HTMLElement>);
  });

  useEffect(() => {
    if (!isWeb && isOSThemeSupported()) {
      dispatch(generalSlice.actions.setUseOSThemeEnabled(true));
    }
  }, [dispatch, isWeb]);

  useEffect(() => {
    let vConsole: any;
    if (!isWeb && appEnvironment === ENVIRONMENT.Staging) {
      // @ts-ignore
      import('vconsole').then(VConsoleModule => {
        const VConsole = VConsoleModule.default;
        vConsole = new VConsole();
      });
    }
    return () => {
      vConsole?.destroy?.();
    };
  }, [isWeb]);

  useBatchDeeplinks();
  useFetchCardsAndBackgrounds();

  useEffect(() => {
    if (!waitingUserSubscription) {
      analytics.openPage(isSubscribed!);
    }
  }, [isSubscribed, waitingUserSubscription]);

  return (
    <UploadDropzone onDragActive={setIsDragActive} isDragDisabled={!!(activeTool && activeTool !== BATCH_TOOLS.upload)}>
      {eraserItem && <BatchEraser />}
      <Layout
        header={<Header />}
        toolbar={((activeTool === BATCH_TOOLS.upload || !activeTool) && !IS_ONE_TOOL) && (
          <SidebarTools setSidebarOpen={setSidebarOpen} />
        )}
        dynamic={activeTool && activeTool !== BATCH_TOOLS.upload && (
          <DynamicContainer
            customHeight={bottomSheetCustomHeight}
            isOpen={isSidebarOpen}
            onExpand={setExpandState}
            toggle={() => setSidebarOpen((prev: Boolean) => !prev)}
          >
            <Sidebar />
          </DynamicContainer>
        )}
        content={(
          <Grid
            minHeight="100%"
            gap={Spacings.s12}
            autoFlow="column"
            columns="1fr"
            padding={Spacings.s24}
          >
            <MainContent showDragContent={isDragActive} />
          </Grid>
        )}
      />
      <SuccessNotificationUI />
      {!isBatchEditorFirstOpen && <InfoPopup data={batchEditorInfoPopupData} isGeneralMessage={false} />}
      {isWeb && <ThemeSettings />}
      <PreviewModal />
    </UploadDropzone>
  );
}

export default memo(Batch);
